import { useState, useMemo } from 'react';
import { lookupPincode, deliveryCharge, isValidPincode, amountToFreeDelivery } from '../data/pincodes';
import { DELIVERY, BUSINESS } from '../data/pricing';
import { formatINR } from '../lib/quote';
import { deliveryLabel } from '../lib/deliveryDate';
import { savedPincode, savePincode } from '../lib/customer';
import { track, FUNNEL } from '../lib/track';

// Product page: "Deliver to 110049 · Delivery by tomorrow, Tue 6 Oct".
//
// Every large Indian store answers the two questions a buyer has before
// paying (do you deliver here, and when) right under the price. The pincode
// is remembered, so the cart and checkout already know it and never ask
// twice.

const Pin = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-blue-700" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

export default function DeliveryCheck({ price, qty = 1, inStock, leadTime = '5 to 7 days' }) {
  const [pincode, setPincode] = useState(() => savedPincode());
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(() => !savedPincode());
  const [error, setError] = useState('');

  const pin = useMemo(() => (pincode ? lookupPincode(pincode) : null), [pincode]);
  const charge = pin?.ok ? deliveryCharge(pin, { orderValue: (price || 0) * qty, units: qty }) : null;
  const freeAbove = amountToFreeDelivery(0);

  const check = () => {
    if (!isValidPincode(draft)) {
      setError('Enter a 6-digit pincode');
      return;
    }
    const r = lookupPincode(draft);
    track(FUNNEL.PINCODE_CHECKED, { pincode: r.pin, serviceable: r.ok, zone: r.zone, source: 'product_page' });
    if (!r.ok) {
      setError(r.message || 'We do not deliver to this pincode yet.');
      return;
    }
    savePincode(r.pin);
    setPincode(r.pin);
    setEditing(false);
    setError('');
  };

  if (editing || !pin?.ok) {
    return (
      <div className="mt-4 rounded-xl border border-gray-200 bg-white p-3.5">
        <label htmlFor="pdp-pincode" className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <Pin />
          Check delivery date and charge
        </label>
        <div className="mt-2.5 flex gap-2">
          <input
            id="pdp-pincode"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={6}
            value={draft}
            onChange={(e) => { setDraft(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') check(); }}
            placeholder="Enter pincode"
            className="h-11 min-w-0 flex-1 rounded-lg border border-gray-300 px-3 text-base outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
          <button type="button" onClick={check}
            className="h-11 shrink-0 rounded-lg bg-gray-900 px-4 text-sm font-semibold text-white transition hover:bg-gray-700">
            Check
          </button>
        </div>
        {error && (
          <p className="mt-2 text-xs text-red-600">
            {error}{' '}
            {isValidPincode(draft) && (
              <a href={`https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(`Hi, do you deliver to pincode ${draft}?`)}`} target="_blank" rel="noopener noreferrer"
                className="font-semibold text-green-700 underline">WhatsApp us</a>
            )}
          </p>
        )}
        {pincode && !error && (
          <button type="button" onClick={() => setEditing(false)}
            className="mt-2 text-xs font-medium text-gray-500 underline underline-offset-2">
            Keep {pincode}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-white p-3.5">
      <div className="flex items-center justify-between gap-3">
        <p className="flex min-w-0 items-center gap-2 text-sm font-semibold text-gray-900">
          <Pin />
          <span className="truncate">Deliver to {pin.pin}, {pin.city}</span>
        </p>
        <button type="button" onClick={() => { setDraft(''); setEditing(true); }}
          className="shrink-0 text-sm font-semibold text-blue-700">
          Change
        </button>
      </div>
      <div className="mt-2 space-y-0.5 pl-6">
        {inStock ? (
          <p className="text-sm font-semibold text-green-700">Delivery by {deliveryLabel()}</p>
        ) : (
          <p className="text-sm font-semibold text-amber-700">Ordered from Daikin for you: delivery in {leadTime}</p>
        )}
        <p className="text-xs text-gray-600">
          {charge?.free
            ? 'Free delivery on this order'
            : <>Delivery {formatINR(charge?.price)}{freeAbove ? <> · Free on orders above {formatINR(freeAbove)}</> : null}</>}
        </p>
        {inStock && <p className="text-[11px] text-gray-500">{DELIVERY.footnote}</p>}
      </div>
    </div>
  );
}
